import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type ToastVariant = 'error' | 'success' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  /** ms before auto-dismiss. Error toasts default longer since they often carry an action. */
  duration?: number;
  action?: ToastAction;
}

interface ActiveToast extends Required<Pick<ToastOptions, 'message' | 'variant'>> {
  id: number;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  /** Convenience wrapper for the app's most common case per 03_APP_FLOW.md §11:
   * "Network/auth error: toast notification + non-destructive retry." */
  showError: (message: string, onRetry?: () => void) => void;
  showSuccess: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, { bg: string; text: string; icon: string }> = {
  error: { bg: 'bg-error-container', text: 'text-on-error-container', icon: 'error' },
  success: { bg: 'bg-secondary', text: 'text-on-secondary', icon: 'check_circle' },
  info: { bg: 'bg-surface-white border-2 border-ink-muted/20', text: 'text-on-background', icon: 'info' },
};

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  error: 6000, // longer — error toasts often carry a retry action worth reading
  success: 3000,
  info: 4000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, variant = 'info', duration, action }: ToastOptions) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message, variant, action }]);
      setTimeout(() => dismiss(id), duration ?? DEFAULT_DURATION[variant]);
    },
    [dismiss]
  );

  const showError = useCallback(
    (message: string, onRetry?: () => void) => {
      showToast({
        message,
        variant: 'error',
        action: onRetry ? { label: 'Retry', onClick: onRetry } : undefined,
      });
    },
    [showToast]
  );

  const showSuccess = useCallback((message: string) => showToast({ message, variant: 'success' }), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showError, showSuccess }}>
      {children}

      {/* Fixed toast stack — bottom-right, matches existing ProgressView toast position/style */}
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm w-[calc(100%-3rem)] sm:w-auto"
      >
        {toasts.map((t) => {
          const style = VARIANT_STYLES[t.variant];
          return (
            <div
              key={t.id}
              className={`${style.bg} ${style.text} px-5 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-fade-in`}
            >
              <span
                className="material-symbols-outlined flex-shrink-0"
                style={{ fontVariationSettings: "'FILL' 1" }}
                aria-hidden="true"
              >
                {style.icon}
              </span>
              <span className="font-medium text-body-sm flex-1">{t.message}</span>
              {t.action && (
                <button
                  onClick={() => {
                    t.action!.onClick();
                    dismiss(t.id);
                  }}
                  className="font-semibold text-body-sm underline underline-offset-2 flex-shrink-0"
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="flex-shrink-0 opacity-70 hover:opacity-100"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden="true">
                  close
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
